"use client";

import { useTranslation } from "@/app/i18n/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "../../../ui/input";
import { cn } from "@/lib/utils";
import { Button } from "../../../ui/button";
import { useEffect, useState } from "react";
import OTPForm from "../otp-form";
import SetPasswordForm from "../set-password-form";
import { forgotPassword } from "@/actions";
import LocaleLink from "@/components/locale-link";
import { Separator } from "@radix-ui/react-separator";
import { Link } from "lucide-react";
import { EmailValidatorSchema, emailValidatorSchema } from "@/lib/validators/forgot-password-member-id2";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { useContext } from "react";
import { Loader2 } from "lucide-react";
interface ForgotPasswordFormProps {
    forgotPasswordSuccess?: (success: boolean, memberCode: string) => void;
    radiusStyle: any;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ forgotPasswordSuccess, radiusStyle }) => {
    const { t } = useTranslation();

    const [step, setStep] = useState("check-member-id");
    const [resetPasswordToken, setResetPasswordToken] = useState("");
    const [mobileOtpValue, setMobileOtpValue] = useState("");
    const [memberId, setMemberId] = useState("");
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [otpResponseData, setOtpResponseData] = useState({
        ref: "",
        transId: "",
    });
    const form = useForm<EmailValidatorSchema>({
        resolver: zodResolver(emailValidatorSchema),
    });

    useEffect(() => {
        if (otpResponseData.ref != "") {
            setStep("otp");
        }
    }, [otpResponseData, memberId]);
    const colorStyle = useContext(ColorStyleContext);

    const onSubmit = (values: EmailValidatorSchema) => {
        console.log(`member ID::::`, values.email);
        setIsLoading(true);
        setMemberId(values.email);
        forgotPassword(values.email)
            .then((res: any) => {
                if (res) {
                    // setIsLoading(false);
                    if (res.result.success) {
                        if (res.result.skip == true) {
                            setStep("setPassword");
                        } else {
                            setOtpResponseData({
                                ref: res.result.ref,
                                transId: res.result.transId,
                            });
                            setMobileOtpValue(res.result.phone);
                        }
                    } else {
                        setStep("notfound_information");
                    }
                }
            })
            .catch((error: any) => {
                setStep("notfound_information");
                // toast.error(`${t("forgot_password.toast_failed_message")}`, {
                //     position: "bottom-center",
                //     duration: 3000
                // });
            })
            .finally(() => {
                setTimeout(() => {
                    setIsLoading(false);
                }, 3000);
            });
    };

    const handleOTPStepUpdate = (otpSuccess: boolean, result: any) => {
        if (otpSuccess) {
            setStep("setPassword");
            setResetPasswordToken(result.jwt);
        }
    };

    const handleNewPasswordStepUpdate = (setNewPasswordSuccess: boolean) => {
        if (setNewPasswordSuccess && forgotPasswordSuccess) forgotPasswordSuccess(true, memberId);
    };

    return (
        <>
            {step === "check-member-id" && (
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-6">
                        {/* <FormLabel className="text-lg text-c4">{t("forgot_password.forgot_text_check_id")}</FormLabel> */}
                        <FormDescription
                            className="font-light text-sm"
                            style={{ color: colorStyle?.text || "#3d3d3d" }}>
                            {t("forgot_password.forgot_text_confirm_check_id")}
                        </FormDescription>
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field, formState }) => (
                                <FormItem>
                                    <FormLabel
                                        className="form-input-label"
                                        style={{
                                            color: colorStyle?.text || "#3d3d3d",
                                        }}>
                                        {t("forgot_password.forgot_label_id")}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            value={field.value || ""}
                                            className={cn("form-input", formState.errors.email && "form-input-error")}
                                        />
                                    </FormControl>
                                    <FormMessage translate="yes" />
                                </FormItem>
                            )}
                        />
                        <div className="w-full flex flex-col items-center">
                            <button
                                type="submit"
                                className="w-2/3 max-w-[260px] h-[48px] dynamic-bg dynamic-color rounded-full flex items-center justify-center text-lg font-normal"
                                style={
                                    {
                                        "--dynamic-bg": colorStyle?.buttonBgNormal || "#009eca",
                                        "--dynamic-hover-bg": colorStyle?.buttonBgHover || "#254b7c",
                                        "--dynamic-color": colorStyle?.buttonTextNormal || "#ffffff",
                                        "--dynamic-hover-color": colorStyle?.buttonTextHover || "#ffffff",
                                        "--dynamic-focus-color": colorStyle?.buttonTextHover || "#ffffff",
                                        borderTopLeftRadius: radiusStyle?.buttonRadiusTopLeft ?? "6px",
                                        borderTopRightRadius: radiusStyle?.buttonRadiusTopRight ?? "6px",
                                        borderBottomLeftRadius: radiusStyle?.buttonRadiusBottomLeft ?? "6px",
                                        borderBottomRightRadius: radiusStyle?.buttonRadiusBottomRight ?? "6px",
                                    } as React.CSSProperties
                                }>
                                {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                {t("forgot_password.forgot_btn_id_check_submit")}
                            </button>
                        </div>
                    </form>
                </Form>
            )}

            {step === "otp" && (
                <OTPForm
                    numDigits={6}
                    type="email"
                    value={mobileOtpValue}
                    displayValue={memberId}
                    data={otpResponseData}
                    onOTPSubmit={handleOTPStepUpdate}
                    useCase="forgot-password"
                />
            )}
            {step === "setPassword" && (
                <SetPasswordForm
                    username={memberId}
                    onUpdatePassword={handleNewPasswordStepUpdate}
                    resetPasswordToken={resetPasswordToken}
                />
            )}
            {step === "notfound_information" && (
                <div className="flex flex-col justify-center items-center gap-3 w-full ">
                    <div className="border-[1px] xs:border-none border-primary-200 flex flex-col justify-center items-center m-4 p-4 gap-1 rounded-[20px] overflow-clip">
                        <h2 className="font-normal text-lg md:text-xl">
                            {t("verify_member.verfy_information_notfound")}
                        </h2>
                    </div>
                </div>
            )}
        </>
    );
};

export default ForgotPasswordForm;
